import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Sparkles, Plus, Edit, Trash2, Pin, Loader2, Mail, Send, Users as UsersIcon } from 'lucide-react';
import { format } from 'date-fns';
import PushUpdateCard from '@/components/changelog/PushUpdateCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  entry_id: '',
  title: '',
  summary: '',
  body_markdown: '',
  category: 'feature',
  published_at: '',
  is_pinned: false,
  send_on_publish: false,
  sent_on_publish_at: '',
  sent_on_publish_count: 0,
};

const DigestCard = ({ token }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/changelog/digest/config`, { headers });
      setCfg(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load digest config');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/changelog/digest/config`, cfg, { headers });
      setCfg(r.data);
      toast.success(cfg.enabled ? 'Digest armed' : 'Digest disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!window.confirm(`This will email the last 31 days of entries to the current audience. Continue?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/changelog/digest/send-now`, {}, { headers });
      if (r.data.error) {
        toast.error(r.data.error);
      } else {
        toast.success(`Sent to ${r.data.sent}/${r.data.total_recipients} recipients (${r.data.entries} entries)`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  if (loading) return (
    <Card className="border-[#E5E7EB]"><CardContent className="p-4 text-xs text-[#9CA3AF]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading digest config…</CardContent></Card>
  );
  if (!cfg) return null;

  return (
    <Card className="border-[#E5E7EB]" data-testid="changelog-digest-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#002FA7]" />Monthly "What shipped" Digest
            {cfg.enabled ? (
              <Badge className="text-[10px] bg-[#34C759]/15 text-[#34C759] border-0" data-testid="digest-armed-badge">Armed</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-[#9CA3AF]" data-testid="digest-disabled-badge">Disabled</Badge>
            )}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={sendNow} disabled={sending} data-testid="digest-send-now-btn">
            {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}Send Now
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-[#4B5563]">
          On the configured day every month, KStreak emails a branded "What shipped" digest containing every entry from the last 31 days. Keeps users engaged with features they might otherwise miss.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] text-[#4B5563]">Day of month (1-28)</Label>
            <Input
              type="number" min={1} max={28}
              value={cfg.day_of_month}
              onChange={e => setCfg({ ...cfg, day_of_month: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })}
              data-testid="digest-day-input"
            />
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563]">Hour (UTC)</Label>
            <select
              className="w-full mt-1 border border-[#E5E7EB] rounded px-2 py-1.5 text-sm"
              value={cfg.hour}
              onChange={e => setCfg({ ...cfg, hour: Number(e.target.value) })}
              data-testid="digest-hour-select"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563] flex items-center gap-1"><UsersIcon className="w-3 h-3" />Audience</Label>
            <Select value={cfg.audience} onValueChange={v => setCfg({ ...cfg, audience: v })}>
              <SelectTrigger data-testid="digest-audience-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_users">All active users</SelectItem>
                <SelectItem value="admins_only">Admins + HR only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563]">Recipient override (CSV)</Label>
            <Input
              value={cfg.recipient_override_csv || ''}
              onChange={e => setCfg({ ...cfg, recipient_override_csv: e.target.value })}
              placeholder="leave blank to use audience"
              data-testid="digest-override-input"
            />
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-[#F3F4F6]">
          <label className="flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!cfg.enabled}
              onChange={e => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="digest-enabled-toggle"
            />
            Arm digest (send monthly)
          </label>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="digest-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
          </Button>
        </div>
        {cfg.last_run_at && (
          <div className="p-2 bg-[#FAFAFA] rounded text-[11px] text-[#4B5563]" data-testid="digest-last-run">
            Last run <strong>{format(new Date(cfg.last_run_at), 'MMM d, HH:mm')}</strong> UTC —{' '}
            <strong className="text-[#0A0A0A]">{cfg.last_run_sent}</strong> email{cfg.last_run_sent === 1 ? '' : 's'} sent,{' '}
            <strong className="text-[#0A0A0A]">{cfg.last_run_entries}</strong> entries
            {cfg.last_run_error && <span className="text-[#FF3B30]"> · error: {cfg.last_run_error.substring(0, 140)}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminChangelogPage = () => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/changelog/admin/all`, { headers, params: { include_drafts: true, include_deleted: false, limit: 200 } });
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      published_at: new Date().toISOString().slice(0, 16),
    });
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setForm({
      entry_id: e.entry_id,
      title: e.title || '',
      summary: e.summary || '',
      body_markdown: e.body_markdown || '',
      category: e.category || 'feature',
      published_at: e.published_at ? e.published_at.slice(0, 16) : '',
      is_pinned: !!e.is_pinned,
      // On edit, default OFF unless it's never been sent; admin opts back in explicitly.
      send_on_publish: false,
      sent_on_publish_at: e.sent_on_publish_at || '',
      sent_on_publish_count: e.sent_on_publish_count || 0,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.summary.trim()) {
      toast.error('Title and summary are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        summary: form.summary.trim(),
        body_markdown: form.body_markdown,
        category: form.category,
        published_at: form.published_at ? new Date(form.published_at).toISOString() : new Date().toISOString(),
        is_pinned: !!form.is_pinned,
        send_on_publish: !!form.send_on_publish,
      };
      if (form.entry_id) {
        await axios.put(`${API}/changelog/${form.entry_id}`, payload, { headers });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/changelog`, payload, { headers });
        toast.success('Entry published');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this entry? This is a soft-delete — audit history is preserved.')) return;
    try {
      await axios.delete(`${API}/changelog/${id}`, { headers });
      toast.success('Entry removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 lg:p-0 space-y-4" data-testid="admin-changelog-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#002FA7]" />
            <h1 className="text-xl font-semibold text-[#0A0A0A]">Changelog Publishing</h1>
          </div>
          <p className="text-xs text-[#4B5563] mt-1">
            Publish customer-facing "What's New" entries. Shows up in the header popover, `/whats-new`, and the Dashboard card.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="new-entry-btn">
          <Plus className="w-4 h-4 mr-1" />New Entry
        </Button>
      </div>

      <DigestCard token={token} />
      <PushUpdateCard token={token} />

      <Card className="border-[#E5E7EB]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">All entries ({entries.length})</CardTitle>
        </CardHeader>        <CardContent className="p-0">
          {loading && <div className="p-4 text-xs text-[#9CA3AF]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading…</div>}
          {!loading && entries.length === 0 && (
            <div className="p-8 text-center">
              <Sparkles className="w-10 h-10 mx-auto text-[#E5E7EB] mb-2" />
              <p className="text-sm text-[#9CA3AF]">No entries yet. Click "New Entry" to publish your first.</p>
            </div>
          )}
          <div className="divide-y divide-[#F3F4F6]" data-testid="admin-entries-list">
            {entries.map(e => {
              const isFuture = e.published_at > new Date().toISOString();
              return (
                <div key={e.entry_id} className="flex items-center gap-3 p-3 hover:bg-[#FAFAFA]" data-testid={`admin-entry-${e.entry_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className="text-[10px] capitalize">{e.category}</Badge>
                      {e.is_pinned && <Badge variant="outline" className="text-[10px] text-[#FF9500] border-[#FF9500]/40"><Pin className="w-2.5 h-2.5 mr-0.5" />Pinned</Badge>}
                      {isFuture && <Badge variant="outline" className="text-[10px] text-[#9CA3AF]">Scheduled</Badge>}
                      {e.sent_on_publish_at && (
                        <Badge variant="outline" className="text-[10px] text-[#34C759] border-[#34C759]/40" data-testid={`sent-badge-${e.entry_id}`}>
                          <Send className="w-2.5 h-2.5 mr-0.5" />Notified &middot; {e.sent_on_publish_count}
                        </Badge>
                      )}
                      <span className="text-[10px] text-[#9CA3AF]">{e.published_at ? format(new Date(e.published_at), 'MMM d, yyyy HH:mm') : ''}</span>
                    </div>
                    <p className="text-sm font-medium text-[#0A0A0A] mt-0.5 truncate">{e.title}</p>
                    <p className="text-[11px] text-[#4B5563] truncate">{e.summary}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-${e.entry_id}`}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e.entry_id)} className="text-[#FF3B30]" data-testid={`delete-${e.entry_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[640px]" data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle>{form.entry_id ? 'Edit entry' : 'Publish new entry'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] text-[#4B5563]">Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Ontology Assist for PKM Authors" data-testid="form-title" />
            </div>
            <div>
              <Label className="text-[11px] text-[#4B5563]">Summary * <span className="text-[#9CA3AF]">(shown in popover and Dashboard card)</span></Label>
              <Input value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} placeholder="One-line teaser, ≤300 chars" maxLength={300} data-testid="form-summary" />
            </div>
            <div>
              <Label className="text-[11px] text-[#4B5563]">Body (Markdown)</Label>
              <Textarea value={form.body_markdown} onChange={e => setForm({ ...form, body_markdown: e.target.value })} rows={8} placeholder="## Highlights&#10;- bullet one&#10;- bullet two&#10;&#10;Inline **bold** and `code` supported." className="font-mono text-xs" data-testid="form-body" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-[#4B5563]">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v, send_on_publish: form.send_on_publish || v === 'security' })}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">Feature</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="fix">Fix</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-[#4B5563]">Publish at (UTC)</Label>
                <Input type="datetime-local" value={form.published_at} onChange={e => setForm({ ...form, published_at: e.target.value })} data-testid="form-published-at" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer select-none">
              <input type="checkbox" checked={form.is_pinned} onChange={e => setForm({ ...form, is_pinned: e.target.checked, send_on_publish: form.send_on_publish || e.target.checked })} data-testid="form-pinned" />
              Pin to top
            </label>

            {/* send_on_publish — the "notify users immediately" opt-in.
                Auto-suggested when category=security or is_pinned=true, but always overridable. */}
            <div className={`rounded-md p-2.5 border ${form.send_on_publish ? 'bg-[#FF3B30]/5 border-[#FF3B30]/30' : 'bg-[#F9FAFB] border-[#E5E7EB]'}`}>
              <label className="flex items-start gap-2 text-xs text-[#0A0A0A] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.send_on_publish}
                  onChange={e => setForm({ ...form, send_on_publish: e.target.checked })}
                  className="mt-0.5"
                  data-testid="form-send-on-publish"
                />
                <div>
                  <div className="font-semibold flex items-center gap-1">
                    <Send className="w-3 h-3 text-[#FF3B30]" />
                    Email users immediately on publish
                    {(form.category === 'security' || form.is_pinned) && !form.send_on_publish && (
                      <span className="ml-1 text-[10px] font-normal text-[#FF9500]">&middot; recommended for {form.category === 'security' ? 'security' : 'pinned'} entries</span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#4B5563] mt-0.5 leading-snug">
                    Fires a one-off branded email to the digest audience <strong>right now</strong>, independent of the monthly cron.
                    Use for security releases and critical announcements — safe for others to wait for the Monday digest.
                    {form.sent_on_publish_at && (
                      <span className="block mt-1 text-[10px] text-[#34C759]">
                        Already sent once ({form.sent_on_publish_count} email{form.sent_on_publish_count === 1 ? '' : 's'}) &middot; re-sending is a no-op.
                      </span>
                    )}
                  </p>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="form-save">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {form.entry_id ? 'Save changes' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChangelogPage;
